import { Text, View } from "react-native";
import { ArrowRight, CheckCircle2 } from "lucide-react-native";
import { CardSurface, CompactActionButton } from "@louise/moon-ui";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import type { PlanAction } from "../plan.types";
import {
  TimelineActionRow,
  TimelineSubtaskCard,
} from "./TimelineTaskComponents";
import { StepperSection } from "./Stepper";
import { OrganicMiniIcon } from "./OrganicMiniIcon";
import { HorizontalSnapCarousel } from "./HorizontalSnapCarousel";

type OptimizationInput = {
  id: string;
  eyebrow: string;
  title: string;
  body: string;
  bullets: string[];
  cta: PlanAction;
};

function buildDefaultInputs(t: TranslationFunction): OptimizationInput[] {
  return [
    {
      id: "sperm-side",
      eyebrow: t("homepage.review_concept.optimize_default_sperm_eyebrow"),
      title: t("homepage.review_concept.optimize_default_sperm_title"),
      body: t("homepage.review_concept.optimize_default_sperm_body"),
      bullets: [
        t("homepage.review_concept.optimize_default_sperm_bullet_1"),
        t("homepage.review_concept.optimize_default_sperm_bullet_2"),
        t("homepage.review_concept.optimize_default_sperm_bullet_3"),
      ],
      cta: {
        label: t("homepage.review_concept.optimize_default_sperm_cta"),
        screen: "ProgramsScreen",
        params: { focus: "sperm-optimization" },
      },
    },
    {
      id: "egg-side",
      eyebrow: t("homepage.review_concept.optimize_default_egg_eyebrow"),
      title: t("homepage.review_concept.optimize_default_egg_title"),
      body: t("homepage.review_concept.optimize_default_egg_body"),
      bullets: [
        t("homepage.review_concept.optimize_default_egg_bullet_1"),
        t("homepage.review_concept.optimize_default_egg_bullet_2"),
        t("homepage.review_concept.optimize_default_egg_bullet_3"),
      ],
      cta: {
        label: t("homepage.review_concept.optimize_default_egg_cta"),
        screen: "AtHomeFertilityTesting",
      },
    },
  ];
}

type OptimizationVariant =
  "embryo-freezing" | "ivf" | "iui" | "amab-family-building";

function buildIuiInputs(t: TranslationFunction): OptimizationInput[] {
  return [
    {
      id: "timing-side",
      eyebrow: t("homepage.review_concept.optimize_iui_timing_eyebrow"),
      title: t("homepage.review_concept.optimize_iui_timing_title"),
      body: t("homepage.review_concept.optimize_iui_timing_body"),
      bullets: [
        t("homepage.review_concept.optimize_iui_timing_bullet_1"),
        t("homepage.review_concept.optimize_iui_timing_bullet_2"),
        t("homepage.review_concept.optimize_iui_timing_bullet_3"),
      ],
      cta: {
        label: t("homepage.review_concept.optimize_iui_timing_cta"),
        screen: "AtHomeFertilityTesting",
      },
    },
    {
      id: "sperm-side",
      eyebrow: t("homepage.review_concept.optimize_iui_sperm_eyebrow"),
      title: t("homepage.review_concept.optimize_iui_sperm_title"),
      body: t("homepage.review_concept.optimize_iui_sperm_body"),
      bullets: [
        t("homepage.review_concept.optimize_iui_sperm_bullet_1"),
        t("homepage.review_concept.optimize_iui_sperm_bullet_2"),
        t("homepage.review_concept.optimize_iui_sperm_bullet_3"),
      ],
      cta: {
        label: t("homepage.review_concept.optimize_iui_sperm_cta"),
        screen: "AtHomeFertilityTesting",
      },
    },
    {
      id: "tube-uterus",
      eyebrow: t("homepage.review_concept.optimize_iui_tube_eyebrow"),
      title: t("homepage.review_concept.optimize_iui_tube_title"),
      body: t("homepage.review_concept.optimize_iui_tube_body"),
      bullets: [
        t("homepage.review_concept.optimize_iui_tube_bullet_1"),
        t("homepage.review_concept.optimize_iui_tube_bullet_2"),
        t("homepage.review_concept.optimize_iui_tube_bullet_3"),
      ],
      cta: {
        label: t("homepage.review_concept.optimize_iui_tube_cta"),
        screen: "FindCareScreen",
        params: { filter: "iui" },
      },
    },
  ];
}

function buildAmabInputs(t: TranslationFunction): OptimizationInput[] {
  return [
    {
      id: "sperm-side",
      eyebrow: t("homepage.review_concept.optimize_amab_sperm_eyebrow"),
      title: t("homepage.review_concept.optimize_amab_sperm_title"),
      body: t("homepage.review_concept.optimize_amab_sperm_body"),
      bullets: [
        t("homepage.review_concept.optimize_amab_sperm_bullet_1"),
        t("homepage.review_concept.optimize_amab_sperm_bullet_2"),
        t("homepage.review_concept.optimize_amab_sperm_bullet_3"),
      ],
      cta: {
        label: t("homepage.review_concept.optimize_amab_sperm_cta"),
        screen: "ProgramsScreen",
        params: { focus: "sperm-optimization" },
      },
    },
    {
      id: "egg-source",
      eyebrow: t("homepage.review_concept.optimize_amab_egg_eyebrow"),
      title: t("homepage.review_concept.optimize_amab_egg_title"),
      body: t("homepage.review_concept.optimize_amab_egg_body"),
      bullets: [
        t("homepage.review_concept.optimize_amab_egg_bullet_1"),
        t("homepage.review_concept.optimize_amab_egg_bullet_2"),
        t("homepage.review_concept.optimize_amab_egg_bullet_3"),
      ],
      cta: {
        label: t("homepage.review_concept.optimize_amab_egg_cta"),
        screen: "LearnScreen",
        params: { topic: "egg-source" },
      },
    },
    {
      id: "carrier-transfer",
      eyebrow: t("homepage.review_concept.optimize_amab_carrier_eyebrow"),
      title: t("homepage.review_concept.optimize_amab_carrier_title"),
      body: t("homepage.review_concept.optimize_amab_carrier_body"),
      bullets: [
        t("homepage.review_concept.optimize_amab_carrier_bullet_1"),
        t("homepage.review_concept.optimize_amab_carrier_bullet_2"),
        t("homepage.review_concept.optimize_amab_carrier_bullet_3"),
      ],
      cta: {
        label: t("homepage.review_concept.optimize_amab_carrier_cta"),
        screen: "LearnScreen",
        params: { topic: "carrier-path" },
      },
    },
  ];
}

function getOptimizationConfig(
  variant: OptimizationVariant,
  t: TranslationFunction,
) {
  if (variant === "iui") {
    return {
      stepLabel: t("homepage.review_concept.optimize_step_label_iui"),
      title: t("homepage.review_concept.optimize_title_iui"),
      subtitle: t("homepage.review_concept.optimize_subtitle_iui"),
      actionTitle: t("homepage.review_concept.optimize_action_title_iui"),
      actionSubtitle: t("homepage.review_concept.optimize_action_subtitle_iui"),
      items: buildIuiInputs(t),
    };
  }

  if (variant === "amab-family-building") {
    return {
      stepLabel: t("homepage.review_concept.optimize_step_label"),
      title: t("homepage.review_concept.optimize_title_amab"),
      subtitle: t("homepage.review_concept.optimize_subtitle_amab"),
      actionTitle: t("homepage.review_concept.optimize_action_title_amab"),
      actionSubtitle: t(
        "homepage.review_concept.optimize_action_subtitle_amab",
      ),
      items: buildAmabInputs(t),
    };
  }

  const isIvf = variant === "ivf";
  return {
    stepLabel: t("homepage.review_concept.optimize_step_label"),
    title: t(
      isIvf
        ? "homepage.review_concept.optimize_title_ivf"
        : "homepage.review_concept.optimize_title_default",
    ),
    subtitle: t(
      isIvf
        ? "homepage.review_concept.optimize_subtitle_ivf"
        : "homepage.review_concept.optimize_subtitle_default",
    ),
    actionTitle: t(
      isIvf
        ? "homepage.review_concept.optimize_action_title_ivf"
        : "homepage.review_concept.optimize_action_title_default",
    ),
    actionSubtitle: t(
      isIvf
        ? "homepage.review_concept.optimize_action_subtitle_ivf"
        : "homepage.review_concept.optimize_action_subtitle_default",
    ),
    items: buildDefaultInputs(t),
  };
}

function OptimizationInputCard({
  item,
  width,
  equalizedHeight,
  onAction,
}: {
  item: OptimizationInput;
  width: number;
  equalizedHeight?: number;
  onAction: (action: PlanAction) => void;
}) {
  return (
    <CardSurface
      variant="panel"
      style={{
        width,
        minHeight: equalizedHeight ?? 292,
        padding: 15,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 10,
          lineHeight: 13,
          color: EMBER.colors.textLight,
          fontWeight: "700",
          letterSpacing: 0.35,
          textTransform: "uppercase",
        }}
      >
        {item.eyebrow}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 17,
          lineHeight: 21,
          color: EMBER.colors.text,
          fontWeight: "700",
          marginTop: 10,
        }}
      >
        {item.title}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 18,
          color: EMBER.colors.textMid,
          fontWeight: "400",
          marginTop: 6,
        }}
      >
        {item.body}
      </Text>

      <View style={{ gap: 8, marginTop: 12 }}>
        {item.bullets.map((bullet) => (
          <View
            key={bullet}
            style={{ flexDirection: "row", alignItems: "flex-start", gap: 7 }}
          >
            <CheckCircle2
              size={12}
              color={EMBER.colors.textLight}
              strokeWidth={1.8}
              style={{ marginTop: 2 }}
            />
            <Text
              className="font-dm-sans"
              style={{
                flex: 1,
                fontSize: 12,
                lineHeight: 17,
                color: EMBER.colors.textMid,
                fontWeight: "400",
              }}
            >
              {bullet}
            </Text>
          </View>
        ))}
      </View>

      <View style={{ flex: 1 }} />

      <CompactActionButton
        label={item.cta.label}
        action="optimization_card_cta"
        size="sm"
        variant="secondary"
        trailingIcon={ArrowRight}
        onPress={() => onAction(item.cta)}
        style={{ alignSelf: "flex-start", marginTop: 12 }}
      />
    </CardSurface>
  );
}

export function OptimizeInputsStep({
  onAction,
  variant = "embryo-freezing",
}: {
  onAction: (action: PlanAction) => void;
  variant?: OptimizationVariant;
}) {
  const t = useTranslations();
  const config = getOptimizationConfig(variant, t);

  return (
    <StepperSection
      id={`precycle-${variant}.optimize`}
      stepLabel={config.stepLabel}
      showRail={false}
    >
      <TimelineSubtaskCard
        id={`precycle-${variant}.optimize.window`}
        title={config.title}
        subtitle={config.subtitle}
        titleIcon={<OrganicMiniIcon kind="optimization" size={34} />}
      >
        <HorizontalSnapCarousel
          items={config.items}
          equalizeItemHeights
          measureKey={`precycle-${variant}-optimization-inputs-v1`}
          gap={10}
          fallbackCardWidth={252}
          getCardWidth={(containerWidth) =>
            Math.min(270, Math.max(232, containerWidth * 0.78))
          }
          keyExtractor={(item) => item.id}
          renderItem={(item, width, _index, equalizedHeight) => (
            <OptimizationInputCard
              item={item}
              width={width}
              equalizedHeight={equalizedHeight}
              onAction={onAction}
            />
          )}
          contentContainerStyle={{ paddingRight: 4 }}
        />
      </TimelineSubtaskCard>

      <TimelineActionRow
        title={config.actionTitle}
        subtitle={config.actionSubtitle}
        icon={<OrganicMiniIcon kind="concierge" size={34} />}
        onPress={() =>
          onAction({
            label: t("homepage.review_concept.optimize_action_cta"),
            screen: "ProgramsScreen",
          })
        }
      />
    </StepperSection>
  );
}
